import { Link } from 'react-router-dom';
import { Sparkles, Heart, Star } from 'lucide-react';
import ImagePlaceholder from '../components/ImagePlaceholder';
import Footer from '../components/Footer';

export default function Home() {
  const categories = [
    { name: 'Cupcakes', icon: '🧁', description: 'Fluffy and decorative' },
    { name: 'Cakes', icon: '🎂', description: 'Custom layered perfection' },
    { name: 'Cookies', icon: '🍪', description: 'Signature flavors' },
    { name: 'Cake Pops', icon: '🍭', description: 'Cute and customizable' },
  ];

  const favorites = [
    { name: 'Dubai Chocolate Cupcakes', price: '$6.00' },
    { name: 'Tres Leches Cupcakes', price: '$6.00' },
    { name: 'Midnight Mocha Cake', price: 'Starting at $40' },
    { name: 'Dulce De Leche Cookies', price: '$4.00' },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center pt-20"
        style={{
          backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/bfce07a1-7139-48cf-8f8e-534e5eeee823/images/6a0c7155d361afadc185b0e048981cf6bfa2b87b6726c8190265be7b2546d675.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-white/70 backdrop-blur-md"></div>
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <div className="mb-6">
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/bfce07a1-7139-48cf-8f8e-534e5eeee823/images/85ddd4dc33b46d3d987cf147d7153b009d7ab1b445ee1ebb97ee298a87b65bd7.png" 
              alt="Decorative bow" 
              className="w-20 h-20 mx-auto mb-4 opacity-60"
            />
          </div>
          <h1 className="script-font text-5xl sm:text-6xl lg:text-7xl text-[var(--accent-gold)] mb-6" data-testid="hero-title">
            Pearl Pastries
          </h1>
          <p className="text-lg sm:text-xl text-[var(--text-main)] mb-8 leading-relaxed max-w-2xl mx-auto">
            Handcrafted desserts made with love for your special moments. 
            Experience the perfect blend of elegance and sweetness.
          </p>
          <Link 
            to="/ordering"
            className="inline-block px-12 py-4 bg-[var(--primary)] text-[var(--primary-foreground)] rounded-full text-lg font-medium button-hover"
            data-testid="hero-order-button"
          >
            Order Now
          </Link>
        </div>
      </section>

      {/* Featured Categories */}
      <section className="py-16 lg:py-24 px-6 lg:px-12" data-testid="categories-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl mb-4">Our Specialties</h2>
            <p className="text-[var(--text-muted)] text-lg max-w-2xl mx-auto">
              Explore our handcrafted dessert collection
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {categories.map((category) => (
              <div 
                key={category.name}
                className="bg-white rounded-3xl p-8 text-center card-hover border border-[var(--border-soft)]"
                data-testid={`category-${category.name.toLowerCase()}`}
              >
                <div className="text-6xl mb-4">{category.icon}</div>
                <h3 className="text-2xl mb-2">{category.name}</h3>
                <p className="text-[var(--text-muted)] text-sm">{category.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Favorites */}
      <section className="py-16 lg:py-24 px-6 lg:px-12 bg-[var(--background-secondary)]" data-testid="favorites-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl mb-4 flex items-center justify-center gap-3">
              <Heart className="text-[var(--secondary)]" size={36} />
              Customer Favorites
            </h2>
            <p className="text-[var(--text-muted)] text-lg">
              Most loved treats by our community
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {favorites.map((item, index) => (
              <div 
                key={index}
                className="bg-white rounded-3xl p-6 card-hover border border-[var(--border-soft)]"
                data-testid={`favorite-item-${index}`}
              >
                <ImagePlaceholder aspect="square" className="mb-4" />
                <h3 className="text-lg font-medium mb-2">{item.name}</h3>
                <p className="text-[var(--accent-gold)] font-semibold">{item.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Custom Orders CTA */}
      <section className="py-16 lg:py-24 px-6 lg:px-12" data-testid="custom-orders-section">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-[var(--background-secondary)] to-white rounded-3xl p-12 border border-[var(--border-soft)]">
            <Sparkles className="mx-auto mb-6 text-[var(--accent-gold)]" size={48} />
            <h2 className="text-3xl sm:text-4xl mb-4">Custom Orders Welcome!</h2>
            <p className="text-[var(--text-muted)] text-lg mb-8 leading-relaxed">
              Have a special vision for your event? We love bringing your dessert dreams to life 
              with personalized designs, colors, and themes.
            </p>
            <Link 
              to="/ordering"
              className="inline-block px-10 py-4 bg-[var(--accent-gold)] text-white rounded-full text-lg font-medium button-hover"
              data-testid="custom-order-button"
            >
              Start Your Custom Order
            </Link>
          </div>
        </div>
      </section>

      {/* Social Preview */}
      <section className="py-16 lg:py-24 px-6 lg:px-12 bg-[var(--background-secondary)]" data-testid="social-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl mb-4">Follow Our Journey</h2>
            <p className="text-[var(--text-muted)] text-lg">
              See what we're baking on Instagram
            </p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <ImagePlaceholder key={i} aspect="square" />
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
